import { difference, flatten, map, reduce, uniq } from 'lodash'
import keysFn from 'lodash/keys'

import i18n from 'modules/core/lib/i18n'

import { AxisNames } from '.'
import DimensionMuncher from './dimensionMuncher'
import Dimensions, { DimensionsGenerator } from './dimensions'

export default class Bar extends DimensionMuncher {
  /**
   * call - This method returns the data structure to print a bar chart.
   *
   * @method
   */
  call() {
    return new Dimensions(this.data, this.axisNames(), [
      this.twoDimensions(),
      this.threeDimensions(),
    ]).call()
  }

  /**
   * axisNames - This method returns an object with three properties xAxis, yAxis and groupBy,
   * if the Quert object does not have a tertiaryField the groupBy property will be null,
   * on the other hand it will return the groupBy property.
   *
   * @method
   */
  axisNames(): AxisNames {
    const { primaryField, secondaryField, tertiaryField } = this.query.fields

    if (!tertiaryField)
      return {
        yAxis: primaryField,
        xAxis: secondaryField,
        groupBy: undefined,
      }

    return {
      yAxis: primaryField,
      xAxis: tertiaryField,
      groupBy: secondaryField,
    }
  }

  /**
   * twoDimensions - This method returns a dimensions generator instance ready
   * to generate a data structure for a Bar chart, the first parameter is a function
   * so as to generate the Y axis and X axis.
   *
   * The second function is a function that takes the result of the first function and maps the
   * data in order to use this data with a Bar chart.
   *
   * @method
   */
  twoDimensions() {
    const { yAxis, xAxis } = this.axisNames()

    return new DimensionsGenerator(
      (item) => {
        return {
          ...item,
          [xAxis]: item[xAxis] ?? i18n.t('common.missing_field'),
          [yAxis]: item[yAxis],
        }
      },
      (processedData) => {
        return {
          processedData,
          keys: [yAxis],
          indexBy: xAxis,
        }
      }
    )
  }

  /**
   * threeDimensions - This method returns a dimensions generator instance ready
   * to generate a data structure for a Bar chart, the first parameter is a function
   * so as to generate the Y axis and X axis and group the data with the groupBy value.
   *
   * The second function is a function that takes the result of the first function and maps the
   * data in order to use this data with a Bar chart.
   *
   * @method
   */
  threeDimensions() {
    const { yAxis, xAxis, groupBy } = this.axisNames()

    return new DimensionsGenerator(
      (group) => {
        return reduce(
          group,
          (memo: any, item: any) => {
            const xAxisLabel = item[xAxis] || i18n.t('common.missing_field')
            memo[xAxisLabel] = item[yAxis]
            memo[groupBy!] = item[groupBy!] || i18n.t('common.missing_field')

            return memo
          },
          {}
        )
      },
      (processedData) => {
        const dataKeys = uniq(flatten(map(processedData, (item: any) => keysFn(item))))
        const keys: any = difference(dataKeys, [groupBy])

        return {
          processedData,
          keys,
          indexBy: groupBy,
        }
      }
    )
  }
}
